import * as React from 'react';
import { IAuthorizationContext, IAuthorizer, PermissionKey } from '@agilepoint/wat';

const authorizerContext = React.createContext<IAuthorizationContext<PermissionKey>>({} as any);

export const AuthorizationProvider = authorizerContext.Provider;

export function createAuthorizationProvider<TKey extends PermissionKey>() {
    return AuthorizationProvider as React.ExoticComponent<React.ProviderProps<IAuthorizationContext<TKey>>>;
}

export interface IAuthorizeProps {
    accessRight: string;
    children?: JSX.Element;
}

export const Authorize = React.memo((props: IAuthorizeProps) => {
    const { accessRight, children } = props;
    const renderComponent = React.useCallback((context: IAuthorizationContext<PermissionKey>) => (context.authorizer.authorize(accessRight) ? children : null), [accessRight, children]);
    return (
        <authorizerContext.Consumer>
            {renderComponent}
        </authorizerContext.Consumer>
    );
});

Authorize.displayName = 'Authorize';

export function withAuthorizer<TKey extends PermissionKey, TProps extends { authorizer: IAuthorizer<TKey>; }>(
    component: React.ComponentType<TProps>
): React.ComponentType<Omit<TProps, 'authorizer'>> {
    const WithAuthorizer = React.memo((props: Omit<TProps, 'authorizer'>) => {
        const renderer = React.useRef((context: IAuthorizationContext<TKey>) => (
            React.createElement(component, { ...props as any, ...{ authorizer: context.authorizer } })
        ));
        return (
            <authorizerContext.Consumer>
                {renderer.current as any}
            </authorizerContext.Consumer>
        );
    });
    WithAuthorizer.displayName = 'WithAuthorizer';
    return WithAuthorizer as any;
}

export function injectAuthorizationProvider<TKey extends PermissionKey, TProps extends { authorizationContext: IAuthorizationContext<TKey>; }>(component: React.ComponentType<TProps>) {
    return class AuthorizationProviderInjector extends React.Component<TProps> {

        _permissionSubscription?: { dispose: () => void; };
        onPermissionChanged = () => {
            this.forceUpdate();
        }

        override componentDidMount() {
            const { authorizationContext } = this.props;
            this._permissionSubscription = authorizationContext.permissionStore.subscribe(this.onPermissionChanged);
        }

        override componentWillUnmount() {
            this._permissionSubscription?.dispose();
        }

        override render() {
            return (
                <authorizerContext.Provider value={this.props.authorizationContext} >
                    {React.createElement(component, this.props as any)}
                </authorizerContext.Provider>
            );
        }
    };
}

export function useAuthorize() {
    const { authorizer } = React.useContext(authorizerContext);
    return authorizer.authorize;
}
